/**
 * Users Page - Enterprise Admin
 * ==============================
 * صفحة إدارة المستخدمين - نظام شامل
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../lib/ui/Card';
import { Table } from '../../../lib/ui/Table';
import { Button } from '../../../lib/ui/Button';
import { SearchBox } from '../../../lib/ui/SearchBox';
import { Modal } from '../../../lib/ui/Modal';
import { Input } from '../../../lib/ui/Input';
import { Dropdown } from '../../../lib/ui/Dropdown';
import { usersService, rolesService, tenantsService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import './UsersPage.css';

// أيقونات
const Icons = {
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  activity: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
  download: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  key: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>,
};

const UsersPage = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({ full_name: '', email: '', password: '', role_id: '', tenant_id: '' });
  const [editUserData, setEditUserData] = useState({});
  const [passwordData, setPasswordData] = useState({ password: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);

  // إحصائيات
  const stats = useMemo(() => {
    const active = users.filter(u => u.status === 'active').length;
    const suspended = users.filter(u => u.status === 'suspended' || u.status === 'inactive').length;
    const admins = users.filter(u => u.role?.includes('Admin') || u.roles?.includes('admin')).length;
    const online = users.filter(u => u.is_online).length;
    return { total: users.length, active, suspended, admins, online };
  }, [users]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, rolesData, tenantsData] = await Promise.all([
        usersService.getAll().catch(() => ({ items: [] })),
        rolesService.getAll().catch(() => ({ items: [] })),
        tenantsService.getAll().catch(() => ({ items: [] })),
      ]);
      
      const usersList = usersData.items || usersData || [];
      // استخدام البيانات الحقيقية من قاعدة البيانات
      const enrichedUsers = usersList.map(u => ({
        ...u,
        is_online: u.last_login_at ? (new Date() - new Date(u.last_login_at)) < 30 * 60 * 1000 : false,
        last_login: u.last_login_at || u.updated_at,
        role: u.roles?.length > 0 ? u.roles.join(', ') : (u.role_name || 'مستخدم عادي'),
        tenant_name: u.tenant_name || (u.tenant?.name) || '-',
        source: 'database'
      }));
      setUsers(enrichedUsers);
      
      const rolesList = rolesData.items || rolesData || [];
      setRoles(rolesList.map(r => ({ ...r, source: 'database' })));

      const tenantsList = tenantsData.items || tenantsData || [];
      setTenants(tenantsList.map(t => ({ ...t, source: 'database' })));
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.full_name || !newUser.email || !newUser.password) {
      toast.error('جميع الحقول مطلوبة');
      return;
    }

    setSaving(true);
    try {
      await usersService.create(newUser);
      toast.success('تم إضافة المستخدم بنجاح');
      setShowAddModal(false);
      setNewUser({ full_name: '', email: '', password: '', role_id: '', tenant_id: '' });
      loadData();
    } catch (error) {
      toast.error('فشل إضافة المستخدم');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await usersService.delete(selectedUser.id);
      toast.success('تم حذف المستخدم بنجاح');
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error('فشل حذف المستخدم');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    try {
      await usersService.update(user.id, { status: newStatus });
      toast.success(newStatus === 'active' ? 'تم تفعيل المستخدم' : 'تم تعليق المستخدم');
      loadData();
    } catch (error) {
      toast.error('فشل تحديث حالة المستخدم');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserData({
      full_name: user.full_name || '',
      email: user.email || '',
      phone: user.phone || '',
      status: user.status || 'active'
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await usersService.update(selectedUser.id, editUserData);
      toast.success('تم تحديث بيانات المستخدم');
      setShowEditModal(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error('فشل تحديث بيانات المستخدم');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setPasswordData({ password: '', confirmPassword: '' });
    setShowPasswordModal(true);
  };

  const handleUpdatePassword = async () => {
    if (!selectedUser) return;
    if (passwordData.password !== passwordData.confirmPassword) {
      toast.error('كلمات المرور غير متطابقة');
      return;
    }
    if (passwordData.password.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    setSaving(true);
    try {
      await usersService.update(selectedUser.id, { password: passwordData.password });
      toast.success('تم تغيير كلمة المرور');
      setShowPasswordModal(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error('فشل تغيير كلمة المرور');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'اليوم';
    if (days === 1) return 'أمس';
    if (days < 7) return `منذ ${days} أيام`;
    return d.toLocaleDateString('ar-SA');
  };

  const columns = [
    { key: 'full_name', title: 'المستخدم', render: (val, row) => (
      <div className="watrly-users__user-cell">
        <div className={`watrly-users__avatar ${row.is_online ? 'watrly-users__avatar--online' : ''}`}>
          {val?.charAt(0) || 'م'}
        </div>
        <div className="watrly-users__user-info">
          <span className="watrly-users__user-name">{val}</span>
          <span className="watrly-users__user-email">{row.email}</span>
        </div>
      </div>
    )},
    { key: 'role', title: 'الدور', render: (val) => (
      <span className={`watrly-users__role watrly-users__role--${val?.toLowerCase().replace(/\s/g, '-')}`}>
        {val || 'بدون دور'}
      </span>
    )},
    { key: 'tenant_name', title: 'الشركة', render: (val) => (
      <span className="watrly-users__tenant">{val || '-'}</span>
    )},
    { key: 'last_login', title: 'آخر دخول', render: (val) => (
      <span className="watrly-users__last-login">{formatDate(val)}</span>
    )},
    { key: 'status', title: 'الحالة', render: (val, row) => (
      <div className="watrly-users__status-cell">
        <span className={`watrly-users__status watrly-users__status--${val}`}>
          <span className="watrly-users__status-dot" />
          {val === 'active' ? 'نشط' : 'معلق'}
        </span>
        {row.is_online && <span className="watrly-users__online-badge">متصل</span>}
      </div>
    )},
    { key: 'actions', title: '', sortable: false, render: (_, row) => (
      <div className="watrly-users__actions">
        <button 
          className="watrly-users__action-btn watrly-users__action-btn--view"
          onClick={() => navigate(`/admin/users/${row.id}`)}
          title="عرض التفاصيل"
        >
          {Icons.eye}
        </button>
        <button 
          className="watrly-users__action-btn watrly-users__action-btn--edit"
          onClick={() => handleEditUser(row)}
          title="تعديل"
        >
          {Icons.edit}
        </button>
        <button 
          className="watrly-users__action-btn watrly-users__action-btn--key"
          onClick={() => handleChangePassword(row)}
          title="إعادة تعيين كلمة المرور"
        >
          {Icons.key}
        </button>
        <button 
          className={`watrly-users__action-btn watrly-users__action-btn--toggle ${row.status === 'active' ? 'watrly-users__action-btn--suspend' : 'watrly-users__action-btn--activate'}`}
          onClick={() => handleToggleStatus(row)}
          title={row.status === 'active' ? 'تعليق' : 'تفعيل'}
        >
          {row.status === 'active' ? Icons.x : Icons.check}
        </button>
        <button 
          className="watrly-users__action-btn watrly-users__action-btn--delete"
          onClick={() => { setSelectedUser(row); setShowDeleteModal(true); }}
          title="حذف"
        >
          {Icons.trash}
        </button>
      </div>
    )},
  ];

  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const matchesSearch = u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || u.status === statusFilter;
      const matchesRole = roleFilter === 'all' || u.role === roleFilter;
      const matchesTenant = tenantFilter === 'all' || u.tenant_name === tenantFilter;
      return matchesSearch && matchesStatus && matchesRole && matchesTenant;
    });
  }, [users, searchQuery, statusFilter, roleFilter, tenantFilter]);

  const roleOptions = roles.map(r => ({ value: r.id || r.value, label: r.name || r.label }));
  const tenantOptions = tenants.map(t => ({ value: t.id, label: t.name }));

  const statusOptions = [
    { value: 'all', label: 'جميع الحالات' },
    { value: 'active', label: 'نشط' },
    { value: 'suspended', label: 'معلق' },
  ];

  const roleFilterOptions = [
    { value: 'all', label: 'جميع الأدوار' },
    ...roleOptions,
  ];

  const tenantFilterOptions = [
    { value: 'all', label: 'جميع الشركات' },
    ...tenantOptions,
  ];

  return (
    <div className="watrly-users">
      {/* Header */}
      <div className="watrly-users__header">
        <div>
          <h1 className="watrly-users__title">إدارة المستخدمين</h1>
          <p className="watrly-users__subtitle">
            إدارة مستخدمي المنصة وصلاحياتهم • {stats.total} مستخدم
          </p>
        </div>
        <div className="watrly-users__header-actions">
          <Button variant="ghost" onClick={() => {}}>
            {Icons.download}
            تصدير
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            + إضافة مستخدم
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="watrly-users__stats">
        <div className="watrly-users__stat-card">
          <div className="watrly-users__stat-icon watrly-users__stat-icon--primary">
            {Icons.users}
          </div>
          <div className="watrly-users__stat-content">
            <span className="watrly-users__stat-value">{stats.total}</span>
            <span className="watrly-users__stat-label">إجمالي المستخدمين</span>
          </div>
        </div>
        <div className="watrly-users__stat-card">
          <div className="watrly-users__stat-icon watrly-users__stat-icon--success">
            {Icons.check}
          </div>
          <div className="watrly-users__stat-content">
            <span className="watrly-users__stat-value">{stats.active}</span>
            <span className="watrly-users__stat-label">مستخدم نشط</span>
          </div>
        </div>
        <div className="watrly-users__stat-card">
          <div className="watrly-users__stat-icon watrly-users__stat-icon--info">
            {Icons.activity}
          </div>
          <div className="watrly-users__stat-content">
            <span className="watrly-users__stat-value">{stats.online}</span>
            <span className="watrly-users__stat-label">متصل الآن</span>
          </div>
        </div>
        <div className="watrly-users__stat-card">
          <div className="watrly-users__stat-icon watrly-users__stat-icon--warning">
            {Icons.shield}
          </div>
          <div className="watrly-users__stat-content">
            <span className="watrly-users__stat-value">{stats.admins}</span>
            <span className="watrly-users__stat-label">مديرين</span>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <Card padding="md" className="watrly-users__filters-card">
        <div className="watrly-users__filters">
          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="بحث عن مستخدم بالاسم أو البريد..."
            className="watrly-users__search"
          />
          <div className="watrly-users__filter-group">
            <Dropdown
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="الحالة"
            />
            <Dropdown
              options={roleFilterOptions}
              value={roleFilter}
              onChange={setRoleFilter}
              placeholder="الدور"
            />
          </div>
        </div>
        {(statusFilter !== 'all' || roleFilter !== 'all' || searchQuery) && (
          <div className="watrly-users__active-filters">
            <span>الفلاتر النشطة:</span>
            {searchQuery && (
              <span className="watrly-users__filter-tag">
                بحث: {searchQuery}
                <button onClick={() => setSearchQuery('')}>×</button>
              </span>
            )}
            {statusFilter !== 'all' && (
              <span className="watrly-users__filter-tag">
                الحالة: {statusFilter === 'active' ? 'نشط' : 'معلق'}
                <button onClick={() => setStatusFilter('all')}>×</button>
              </span>
            )}
            {roleFilter !== 'all' && (
              <span className="watrly-users__filter-tag">
                الدور: {roleFilter}
                <button onClick={() => setRoleFilter('all')}>×</button>
              </span>
            )}
            <button 
              className="watrly-users__clear-filters"
              onClick={() => { setSearchQuery(''); setStatusFilter('all'); setRoleFilter('all'); }}
            >
              مسح الكل
            </button>
          </div>
        )}
      </Card>

      {/* Results Count */}
      <div className="watrly-users__results-info">
        عرض {filteredUsers.length} من {stats.total} مستخدم
      </div>

      {/* Table */}
      <Card padding="none" className="watrly-users__table-card">
        <Table
          columns={columns}
          data={filteredUsers}
          loading={loading}
          emptyMessage="لا يوجد مستخدمين مطابقين للبحث"
          hoverable
        />
      </Card>

      {/* Add Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="إضافة مستخدم جديد"
        size="md"
        footer={
          <div className="watrly-users__modal-actions">
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>إلغاء</Button>
            <Button variant="primary" onClick={handleAddUser} loading={saving}>إضافة</Button>
          </div>
        }
      >
        <div className="watrly-users__form">
          <Input
            label="الاسم الكامل"
            placeholder="أدخل الاسم"
            value={newUser.full_name}
            onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
            required
          />
          <Input
            label="البريد الإلكتروني"
            type="email"
            placeholder="user@company.com"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            required
          />
          <Input
            label="كلمة المرور"
            type="password"
            placeholder="••••••••"
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            required
          />
          <Dropdown
            label="الدور"
            options={roleOptions}
            value={newUser.role_id}
            onChange={(val) => setNewUser({ ...newUser, role_id: val })}
            placeholder="اختر الدور"
          />
          <Dropdown
            label="الشركة"
            options={tenantOptions}
            value={newUser.tenant_id}
            onChange={(val) => setNewUser({ ...newUser, tenant_id: val })}
            placeholder="اختر الشركة (اختياري)"
          />
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedUser(null); }}
        title={`تعديل بيانات: ${selectedUser?.full_name || ''}`}
        size="md"
        footer={
          <div className="watrly-users__modal-actions">
            <Button variant="ghost" onClick={() => setShowEditModal(false)}>إلغاء</Button>
            <Button variant="primary" onClick={handleUpdateUser} loading={saving}>حفظ</Button>
          </div>
        }
      >
        <div className="watrly-users__form">
          <Input
            label="الاسم الكامل"
            value={editUserData.full_name || ''}
            onChange={(e) => setEditUserData({ ...editUserData, full_name: e.target.value })}
          />
          <Input
            label="البريد الإلكتروني"
            type="email"
            value={editUserData.email || ''}
            onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
          />
          <Input
            label="رقم الهاتف"
            value={editUserData.phone || ''}
            onChange={(e) => setEditUserData({ ...editUserData, phone: e.target.value })}
          />
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>الحالة</label>
            <select
              value={editUserData.status || 'active'}
              onChange={(e) => setEditUserData({ ...editUserData, status: e.target.value })}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #E5E7EB' }}
            >
              <option value="active">نشط</option>
              <option value="suspended">معلق</option>
            </select>
          </div>
        </div>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => { setShowPasswordModal(false); setSelectedUser(null); }}
        title={`تغيير كلمة مرور: ${selectedUser?.full_name || ''}`}
        size="sm"
        footer={
          <div className="watrly-users__modal-actions">
            <Button variant="ghost" onClick={() => setShowPasswordModal(false)}>إلغاء</Button>
            <Button variant="primary" onClick={handleUpdatePassword} loading={saving}>تغيير</Button>
          </div>
        }
      >
        <div className="watrly-users__form">
          <Input
            label="كلمة المرور الجديدة"
            type="password"
            value={passwordData.password}
            onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
          />
          <Input
            label="تأكيد كلمة المرور"
            type="password"
            value={passwordData.confirmPassword}
            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
          />
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setSelectedUser(null); }}
        title="تأكيد الحذف"
        size="sm"
        footer={
          <div className="watrly-users__modal-actions">
            <Button variant="ghost" onClick={() => { setShowDeleteModal(false); setSelectedUser(null); }}>
              إلغاء
            </Button>
            <Button variant="danger" onClick={handleDeleteUser} loading={saving}>
              حذف
            </Button>
          </div>
        }
      >
        <div className="watrly-users__delete-confirm">
          <div className="watrly-users__delete-icon">
            {Icons.trash}
          </div>
          <p>هل أنت متأكد من حذف المستخدم <strong>{selectedUser?.full_name}</strong>؟</p>
          <p className="watrly-users__delete-warning">هذا الإجراء لا يمكن التراجع عنه.</p>
        </div>
      </Modal>
    </div>
  );
};

export default UsersPage;
